import clsx from 'clsx';
import { format } from 'date-fns';
import { useRef } from 'react';
import { useHoverDirty } from 'react-use';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Tooltip } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { IconsFill, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import {
  getInHandDisplayData,
  toInHandComparableDate,
} from 'src/utils/saleUtils';
import { Feature } from 'src/WebApiController';

import { inhandDateContainer } from './InHandDateDisplay.css';

export function InHandDateDisplay({
  date,
  needsToFulfill,
  handleFulfillmentState,
  mustShipByDate,
  showAbsoluteDate = false,
  className,
}: {
  date?: Date | null;
  needsToFulfill?: boolean;
  handleFulfillmentState?: boolean;
  mustShipByDate?: Date | null;
  showAbsoluteDate?: boolean;
  className?: string;
}) {
  const elementRef = useRef<HTMLInputElement>(null);
  const isHovering = useHoverDirty(elementRef);
  const inhand = useContent(ContentId.InHand);
  const ihdTimezoneFix = useUserHasFeature(Feature.IhdTimezoneFix);

  const { isDarkMode } = useSiteTheme();

  const dateLocal = toInHandComparableDate(date, ihdTimezoneFix);
  const mustShipByDateLocal = toInHandComparableDate(
    mustShipByDate,
    ihdTimezoneFix
  );
  const mustShipByBeforeInHand = Boolean(
    dateLocal && mustShipByDateLocal && mustShipByDateLocal < dateLocal
  );

  const { displayText, displayTooltip, alertIcon } = getInHandDisplayData(
    inhand,
    dateLocal,
    new Date(),
    needsToFulfill,
    showAbsoluteDate
  );

  if (!needsToFulfill && handleFulfillmentState) {
    return null;
  }

  return (
    <div
      title={displayTooltip ?? undefined}
      className={clsx(inhandDateContainer, className)}
    >
      {handleFulfillmentState && alertIcon}
      {mustShipByBeforeInHand && (
        <Tooltip
          open={isHovering}
          delayDuration={100}
          contentVariant={isDarkMode ? 'light' : 'dark'}
          triggerContent={
            <div ref={elementRef} style={{ display: 'flex' }}>
              <InfoOutlineIcon
                size={vars.iconSize.s}
                fill={IconsFill.textWarning}
              />
            </div>
          }
        >
          <Content id={ContentId.MustShipByDateEarlierThanInHand} />
        </Tooltip>
      )}
      <span>
        {displayText}
        {mustShipByBeforeInHand && showAbsoluteDate && mustShipByDateLocal && (
          <>
            {' '}
            <FormatContent
              id={FormatContentId.MustShipBySuffix}
              params={[
                format(mustShipByDateLocal, 'MMM d, yyyy', {
                  locale: getLocaleFromLanguageOrCurrent(),
                }),
              ]}
            />
          </>
        )}
      </span>
    </div>
  );
}
